/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const DISMISSED_KEY = "regulens_feedback_dismissed_at";
const DISMISS_DAYS = 30;

/**
 * After the user's 3rd audit, shows a dismissible banner inviting them to
 * leave feedback. "Not now" snoozes for 30 days (client + server-side).
 */
export default function FeedbackPrompt() {
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [body, setBody] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Client-side snooze check first
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const elapsed = (Date.now() - new Date(dismissedAt).getTime()) / 86400000;
      if (elapsed < DISMISS_DAYS) return;
    }
    api.get("/feedback/should-prompt").then(({ data }) => {
      if (data.should_prompt) setShouldPrompt(true);
    }).catch(() => {});
  }, []);

  if (!shouldPrompt) return null;

  const handleDismiss = async () => {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    setShouldPrompt(false);
    try { await api.post("/feedback/dismiss"); } catch {}
  };

  const handleSubmit = async () => {
    if (rating < 1) return toast.error("Please select a star rating");
    if (!consent) return toast.error("Please confirm consent to proceed");
    setSubmitting(true);
    try {
      await api.post("/feedback/submit", { rating, body, consent });
      toast.success("Thank you for your feedback");
      setOpen(false);
      setShouldPrompt(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage)]/10 px-4 py-3 mb-6 flex items-center justify-between gap-3 flex-wrap"
        data-testid="feedback-prompt-banner"
      >
        <div className="text-sm text-[var(--rg-text-strong)]">
          <span className="font-semibold">How is Regulens working for your store?</span>
          <span className="text-[var(--rg-text)] ml-2">We&apos;d love to hear from you.</span>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-[var(--rg-sage)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-sage)] font-semibold"
            data-testid="feedback-open-btn"
          >
            Share Feedback
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDismiss}
            className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
            data-testid="feedback-dismiss-btn"
          >
            Not now
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="feedback-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Share your feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--rg-text)] mb-2">Rate your experience</div>
              <div className="flex gap-1.5" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1"
                    data-testid={`feedback-star-${s}`}
                  >
                    <Star
                      className="w-7 h-7"
                      strokeWidth={1.5}
                      fill={(hoverRating || rating) >= s ? "var(--rg-amber)" : "transparent"}
                      stroke={(hoverRating || rating) >= s ? "var(--rg-amber)" : "var(--rg-muted)"}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--rg-text)] mb-1">Comments (optional)</div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
                data-testid="feedback-body"
                placeholder="Tell us what you think…"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="feedback-consent-label">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="border-[var(--rg-border-hi)] mt-0.5" />
              <span className="text-xs text-[var(--rg-text)] leading-relaxed">
                I consent to Regulens reviewing this feedback. I understand my comments will not be made public unless I am later asked and confirm.
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="feedback-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating < 1 || !consent}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
              data-testid="feedback-submit"
            >
              {submitting ? "Submitting…" : "Submit Feedback"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
